import classnames from 'classnames';
import themeToClassNames from './themeToClassNames';
import filterXSS from './filterXSS';
import htmlEntitiesDecode from './htmlEntitiesDecode';

import type { SupportedHtmlTags, ElementProps } from './types';

const REGEX_DETECT_HTML = /<[a-z][\s\S]*>/i;
function Element<Tag extends keyof SupportedHtmlTags>(
  /*
    Using an Intersection Type(https://www.typescriptlang.org/docs/handbook/unions-and-intersections.html) to add
    the correct properties for the Component type.

    <Element Component="img" /> ----> TypeScript will complain that there aren't src and alt tags which are required on an <img/> tag
    <Element Component="img" src="https://...." alt="some alt text" ----> TypeScript is ok and you know you've passed in the required props for an img tag
  */
  props: {
    Component?: Tag;
  } & SupportedHtmlTags[Tag] &
    ElementProps
) {
  const {
    Component = 'div',
    children,
    theme = {},
    className,
    forwardedRef,
    qeId,
    tools: _tools,
    ...attributes
  } = props;
  const ComponentRender = Component as React.ElementType;
  const formattedChildren =
    typeof children === 'string'
      ? htmlEntitiesDecode(children as string)
      : children;

  if (
    typeof formattedChildren === 'string' &&
    REGEX_DETECT_HTML.test(formattedChildren)
  ) {
    return (
      <ComponentRender
        {...attributes}
        className={classnames(className, themeToClassNames(theme))}
        dangerouslySetInnerHTML={{
          __html: filterXSS(htmlEntitiesDecode(formattedChildren as string)),
        }}
        ref={forwardedRef}
        data-qe-id={qeId}
      />
    );
  }

  return (
    <ComponentRender
      {...attributes}
      className={classnames(className, themeToClassNames(theme))}
      data-qe-id={qeId}
      ref={forwardedRef}
    >
      {formattedChildren}
    </ComponentRender>
  );
}

export default Element;
